import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { Platform, StyleSheet, useColorScheme } from 'react-native';
import AsyncStorage from '@react-native-async-storage/async-storage';
import { Palette, paletteFor, ThemeMode, ThemePreference } from '../assets/theme';

const STORAGE_KEY = 'ui_theme_preference';

type ThemeContextValue = {
  colors: Palette;
  mode: ThemeMode;
  isDark: boolean;
  preference: ThemePreference;
  setPreference: (preference: ThemePreference) => void;
  toggle: () => void;
};

const ThemeContext = createContext<ThemeContextValue>({
  colors: paletteFor('light'),
  mode: 'light',
  isDark: false,
  preference: 'system',
  setPreference: () => {},
  toggle: () => {},
});

const isPreference = (value: unknown): value is ThemePreference =>
  value === 'light' || value === 'dark' || value === 'system';

type WebStorage = { getItem: (key: string) => string | null };
type WebDocument = {
  documentElement: { style: { colorScheme: string }; setAttribute: (name: string, value: string) => void };
  body?: { style: { backgroundColor: string; color: string } };
  querySelector: (selector: string) => { setAttribute: (name: string, value: string) => void } | null;
  head: { appendChild: (node: unknown) => void };
  createElement: (tag: string) => { setAttribute: (name: string, value: string) => void };
};

/** On web, read the saved preference synchronously to avoid a light→dark flash. */
function readInitialPreference(): ThemePreference {
  if (Platform.OS !== 'web') return 'system';
  try {
    const storage = (globalThis as { localStorage?: WebStorage }).localStorage;
    const stored = storage?.getItem(STORAGE_KEY);
    return isPreference(stored) ? stored : 'system';
  } catch {
    return 'system';
  }
}

/** Keeps the browser chrome (scrollbars, overscroll, theme-color) in sync with the app theme. */
function syncWebDocument(colors: Palette) {
  if (Platform.OS !== 'web') return;
  const doc = (globalThis as { document?: WebDocument }).document;
  if (!doc) return;
  doc.documentElement.style.colorScheme = colors.mode;
  doc.documentElement.setAttribute('data-theme', colors.mode);
  if (doc.body) {
    doc.body.style.backgroundColor = colors.background;
    doc.body.style.color = colors.textPrimary;
  }
  let meta = doc.querySelector("meta[name='theme-color']");
  if (!meta) {
    const created = doc.createElement('meta');
    created.setAttribute('name', 'theme-color');
    doc.head.appendChild(created);
    meta = created;
  }
  meta.setAttribute('content', colors.background);
}

export function AppThemeProvider({ children }: { children: React.ReactNode }) {
  const systemScheme = useColorScheme();
  const [preference, setPreferenceState] = useState<ThemePreference>(readInitialPreference);

  useEffect(() => {
    if (Platform.OS === 'web') return;
    AsyncStorage.getItem(STORAGE_KEY)
      .then((stored) => { if (isPreference(stored)) setPreferenceState(stored); })
      .catch(() => {});
  }, []);

  const setPreference = useCallback((next: ThemePreference) => {
    setPreferenceState(next);
    AsyncStorage.setItem(STORAGE_KEY, next).catch(() => {});
  }, []);

  const mode: ThemeMode = preference === 'system' ? (systemScheme === 'dark' ? 'dark' : 'light') : preference;
  const colors = paletteFor(mode);

  const toggle = useCallback(() => {
    setPreference(mode === 'dark' ? 'light' : 'dark');
  }, [mode, setPreference]);

  useEffect(() => { syncWebDocument(colors); }, [colors]);

  const value = useMemo<ThemeContextValue>(
    () => ({ colors, mode, isDark: mode === 'dark', preference, setPreference, toggle }),
    [colors, mode, preference, setPreference, toggle],
  );

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

export function useAppTheme() {
  return useContext(ThemeContext);
}

/**
 * Theme-aware replacement for a module-level `StyleSheet.create`:
 *
 *   const useStyles = makeStyles((c) => ({ card: { backgroundColor: c.surface } }));
 *   function MyComponent() { const styles = useStyles(); ... }
 *
 * Styles are created once per palette (light/dark) and cached.
 */
export function makeStyles<T extends StyleSheet.NamedStyles<T> | StyleSheet.NamedStyles<any>>(
  factory: (colors: Palette) => T | StyleSheet.NamedStyles<T>,
): () => T {
  const cache = new Map<Palette, T>();
  return function useStyles() {
    const { colors } = useAppTheme();
    let styles = cache.get(colors);
    if (!styles) {
      styles = StyleSheet.create(factory(colors)) as T;
      cache.set(colors, styles);
    }
    return styles;
  };
}
